import * as moment from "moment";

import { AnsweredQuestionWithId, CaseType } from "@teamprevent/common/model";
import { Link } from "@teamprevent/common/task/taskActions/models";

import { TaskType } from "../task/model";
import { LinkedOhCase } from "../crud/ohCase/model";
import { CurrentUser } from "../auth";
import { TeamState } from "../teams";
import { EppDecision } from "../immunisations/model";

export interface TriageState {
    triages: TriageListModel[];
    triage: TriageDetailModel;
    referralTriageTasks: TaskType[];
    ppqTriageTasks: TaskType[];
    physiotherapyReferralTriageTasks: TaskType[];
    genericCaseTriageTasks: TaskType[];
}

export type ReferralTriageListState = TriageState & TeamState & { currentUser: CurrentUser };

export interface TriageListModel {
    readonly id: string;
    readonly caseId: string;
    readonly taskType: string;
    readonly referralCreated: moment.Moment | null;
    readonly dateCreated: moment.Moment | null;
    readonly genericCaseType: string;
    readonly employeeName: string;
    readonly customerName: string;
    readonly customerId: string;
    readonly employeeRecordId: string;
    readonly type: string;
}

export type TriageDetailModel = ReferralTriageDetailModel | PpqTriageDetailModel | PhysiotherapyReferralTriageDetailModel | GenericCaseTriageDetailModel;

export interface ReferralTriageDetailModel {
    triageTaskId: string;
    employeeRecordId: string;
    customerId: string;
    caseId: string;
    description: string;
    consentForReferral: boolean;
    forename: string;
    surname: string;
    dateOfBirth: moment.Moment | null;
    gender: string;
    jobTitle: string;
    lineManagersName: string;
    lineManagerTelephoneNumber: string;
    hrName: string;
    orgLevel1: string;
    standardHoursPerWeek: string;
    absenceDetails: string[];
    lastClinicalActionDate: moment.Moment | null;
    lastReportDate: moment.Moment | null; // – (Date and link to the report)
    lastReportLink: string;
    criticalNotesFromEmployee: string;
    customerNotes: string;
    employeeRecordLink: string;
    supportingDocuments: boolean;
    shiftPattern: string;
    insufficientInformationNotes: string;
    absenceDetailsSection: AnsweredQuestionWithId[];
    reasonsSection: AnsweredQuestionWithId[];
    informationRequiredSection: AnsweredQuestionWithId[];
    managerAttendanceSection: AnsweredQuestionWithId[];
    jobRoleDetailsSection: AnsweredQuestionWithId[];
    additionalQuestionsSection: AnsweredQuestionWithId[];
}

export interface PpqTriageDetailModel {
    managersSection: AnsweredQuestionWithId[];
    employeeSection: AnsweredQuestionWithId[];
    customerId: string;
    caseId: string;
    forename: string;
    surname: string;
    dateOfBirth: moment.Moment | null;
    gender: string;
    jobTitle: string;
    orgLevel1: string;
    description: string;
    employeeRecordLink: string;
    linkedOhCases: LinkedOhCase[];
    customerNotes: string;
    workGroups: Link[];
}

export interface PhysiotherapyReferralTriageDetailModel {
    triageTaskId: string;
    employeeRecordId: string;
    customerId: string;
    caseId: string;
    description: string;
    origin: string;

    // Data Privacy
    privacyConsent: boolean;
    employeeAwareConsent: boolean;
    dataSecureConsent: boolean;
    consentForReferral: boolean;

    // Manager Details
    lineManagerName: string;
    lineManagerEmail: string;
    lineManagerPhone: string;

    // Employee Details
    referredTitle: string;
    referredFirstName: string;
    referredLastName: string;
    referredDob: moment.Moment | null;
    referredGender: string;
    employeeId: string;
    jobTitle: string;
    postcode: string;
    emailAddress: string;
    mobilePhone: string;
    homePhone: string;
    orgLevel1Name: string;
    orgLevel2Name: string;
    orgLevel3Name: string;
    orgLevel4Name: string;

    // Others
    insufficientInformationNotes: string;
    criticalNotesFromEmployee: string;
    customerNotes: string;
    lastReportDate: moment.Moment | null; // – (Date and link to the report)
    employeeRecordLink: string;

    // Section's ids
    lineManagerHrSectionId: AnsweredQuestionWithId[];
    employeeDetailSectionId: AnsweredQuestionWithId[];
    reasonsForReferralSectionId: AnsweredQuestionWithId[];
    attachmentsSectionId: AnsweredQuestionWithId[];
    orgLevelQuestionId: AnsweredQuestionWithId[];
    furtherDetailsSectionId: AnsweredQuestionWithId[];
}

export interface GenericCaseTriageDetailModel {
    serviceRequired: string;
    triageTaskId: string;
    employeeRecordId: string;
    customerId: string;
    caseId: string;
    description: string;
    consentForReferral: boolean;
    forename: string;
    surname: string;
    dateOfBirth: moment.Moment | null;
    gender: string;
    jobTitle: string;
    lineManagersName: string;
    lineManagerTelephoneNumber: string;
    hrName: string;
    orgLevel1: string;
    standardHoursPerWeek: string;
    criticalNotesFromEmployee: string;
    customerNotes: string;
    employeeRecordLink: string;
    supportingDocuments: boolean;
    shiftPattern: string;
    insufficientInformationNotes: string;
}

export interface ImmunisationsClearModel {
    customerId: string;
    caseId: string;
    eppDecision: EppDecision;
    eppNotes: string;
}

export enum PpqOutcomeId {
    unfit = "Unfit",
    temporarilyUnfit = "TemporarilyUnfit",
    fitWithAdjustment = "FitWithAdjustment",
    fit = "Fit",
    temporarilyUnfitPendingFitForWork = "TemporarilyUnfitPendingFitForWork"
}

export interface FiltersModel {
    teams: string[];
    products: CaseType[];
    customers: string[];
    professions: string[];
}

export const ppqOutcomes = [
    { text: "Unfit", value: PpqOutcomeId.unfit },
    { text: "Temporarily Unfit", value: PpqOutcomeId.temporarilyUnfit },
    { text: "Fit with Adjustments", value: PpqOutcomeId.fitWithAdjustment },
    { text: "Temporarily Unfit Pending Fitness for Work Medical", value: PpqOutcomeId.temporarilyUnfitPendingFitForWork },
    { text: "Fit", value: PpqOutcomeId.fit }
];

export const getPpqOutcome = (outcome: string | undefined) => {
    return ppqOutcomes.find(o => o.value === outcome);
};
